/**
 * InstrumentExplorer Utilities
 *
 * Shared utility functions used across InstrumentExplorer components
 */

import type { AssetClass } from '@/types/shared/AssetClass';

/**
 * Utility function to capitalize text appropriately
 * Handles hyphenated words like "investment-grade" -> "Investment-Grade"
 */
export const capitalizeText = (text: string): string => {
  if (!text) return text;
  return text
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('-');
};

/**
 * Handle multi-select filter changes with mutual exclusivity for "all"
 * Ensures "all" and specific selections are mutually exclusive
 */
export const handleFilterSelection = (keys: Set<string>, currentSelection: Set<string>): Set<string> => {
  const keyArray = Array.from(keys);
  const currentArray = Array.from(currentSelection);
  const added = keyArray.find(key => !currentArray.includes(key));
  const removed = currentArray.find(key => !keyArray.includes(key));

  if (added === 'all') return new Set(['all']);
  if (removed === 'all' && keyArray.length === 0) return new Set(['all']);
  if (added && added !== 'all') return new Set(keyArray.filter(key => key !== 'all'));
  if (keyArray.length === 0) return new Set(['all']);
  return new Set(keyArray);
};

/**
 * Format date for display in AG Grid (relative time for recent, absolute for older)
 */
export const formatDate = (date: Date): string => {
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return date.toLocaleDateString();
};

// ===== SHARED FILTERING FUNCTIONS =====

/**
 * Generic interface for items with classification (both Instrument and InstrumentCatalogEntry)
 */
interface ClassifiedItem {
  classification: AssetClass;
  symbol: string;
  name?: string;        // InstrumentCatalogEntry has 'name'
  displayName?: string; // Instrument has 'displayName'
}

/**
 * Check if item matches search term (symbol or name)
 */
export const matchesSearch = (item: ClassifiedItem, searchTerm: string): boolean => {
  if (!searchTerm) return true;
  const term = searchTerm.toLowerCase();
  const name = item.name || item.displayName || '';
  return item.symbol.toLowerCase().includes(term) ||
         name.toLowerCase().includes(term);
};

/**
 * Check if item matches asset class filter
 */
export const matchesAssetClass = (item: ClassifiedItem, assetClassFilter: Set<string>): boolean => {
  return assetClassFilter.has('all') || assetClassFilter.has(item.classification.assetClass);
};

/**
 * Check if item matches market segment filter
 */
export const matchesMarketSegment = (item: ClassifiedItem, marketSegmentFilter: Set<string>): boolean => {
  return marketSegmentFilter.has('all') ||
         (item.classification?.marketSegment && marketSegmentFilter.has(item.classification.marketSegment));
};

/**
 * Check if item matches sub-asset class filter
 */
export const matchesSubAssetClass = (item: ClassifiedItem, subAssetClassFilter: Set<string>): boolean => {
  return subAssetClassFilter.has('all') ||
         (!!item.classification?.subAssetClass && subAssetClassFilter.has(item.classification.subAssetClass));
};

/**
 * Apply all classification-based filters to an item
 * Returns true if item passes all specified filters
 */
export const applyClassificationFilters = (
  item: ClassifiedItem,
  filters: {
    searchTerm: string;
    assetClassFilter: Set<string>;
    marketSegmentFilter: Set<string>;
    subAssetClassFilter: Set<string>;
  }
): boolean => {
  const { searchTerm, assetClassFilter, marketSegmentFilter, subAssetClassFilter } = filters;

  return matchesSearch(item, searchTerm) &&
         matchesAssetClass(item, assetClassFilter) &&
         matchesMarketSegment(item, marketSegmentFilter) &&
         matchesSubAssetClass(item, subAssetClassFilter);
};
